import React, { useState, useEffect } from 'react';
import { Layout, Menu, Typography, Space, Button, Table, Modal, Form, Input, Popconfirm, message } from 'antd';
import { DatabaseOutlined, KeyOutlined, UserOutlined, FileTextOutlined, MenuFoldOutlined, MenuUnfoldOutlined, TableOutlined, PlusOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import DataSources from './DataSources';
import AuthMethods from './AuthMethods';
import TraderProfiles from './TraderProfiles';
import Topics from './Topics';
import TopicTradeTables from './TopicTradeTables';
import Webhooks from './Webhooks';
import TopicWebhooks from './TopicWebhooks';
import BrokerAccounts from './BrokerAccounts';
import BrokerAccountsManager from './BrokerAccountsManager';
import LiveFollows from './LiveFollows';
import UserApproval from './UserApproval';
import { tradeAPI } from '../services/api';

const { Content, Sider } = Layout;
const { Title, Text } = Typography;

const MasterData = () => {
  const location = useLocation();
  const [selectedKey, setSelectedKey] = useState('data-sources');
  const [collapsed, setCollapsed] = useState(false);

  const menuItems = [
    {
      key: 'data-sources',
      icon: <DatabaseOutlined />,
      label: '数据源',
      description: '管理交易数据源配置',
    },
    {
      key: 'auth-methods',
      icon: <KeyOutlined />,
      label: '认证方法',
      description: '数据源认证方式配置',
    },
    {
      key: 'trader-profiles',
      icon: <UserOutlined />,
      label: '交易员资料',
      description: '交易员信息管理',
    },
    {
      key: 'topics',
      icon: <FileTextOutlined />,
      label: '交易话题',
      description: '交易话题和策略管理',
    },
    {
      key: 'topic-trade-tables',
      icon: <DatabaseOutlined />,
      label: '分表管理',
      description: '交易话题分表管理',
    },
    {
      key: 'webhooks',
      icon: <KeyOutlined />,
      label: 'Webhook管理',
      description: 'Discord/钉钉 Webhook管理',
    },
    {
      key: 'topic-webhooks',
      icon: <KeyOutlined />,
      label: '话题Webhook绑定',
      description: '配置交易话题通知要推送到哪些Webhook',
    },
    {
      key: 'broker-accounts',
      icon: <DatabaseOutlined />,
      label: '券商账户管理',
      description: '选择券商后维护对应账户配置',
    },
    {
      key: 'live-follows',
      icon: <TableOutlined />,
      label: '实盘跟随',
      description: '管理实盘跟随配置（按话题同步到券商账户）',
    },
  ];

  const getSelectedItem = () => {
    return menuItems.find(item => item.key === selectedKey);
  };

  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search || '');
      const key = params.get('key');
      if (key && (key === 'user-approval' || menuItems.some(item => item.key === key))) {
        setSelectedKey(key);
      }
    } catch (e) {
      // ignore
    }
  }, [location.search]);

  const renderContent = () => {
    switch (selectedKey) {
      case 'data-sources':
        return <DataSources />;
      case 'auth-methods':
        return <AuthMethods />;
      case 'trader-profiles':
        return <TraderProfiles />;
      case 'topics':
        return <Topics />;
      case 'topic-trade-tables':
        return <TopicTradeTables />;
      case 'webhooks':
        return <Webhooks />;
      case 'topic-webhooks':
        return <TopicWebhooks />;
      case 'broker-accounts':
        return <BrokerAccountsManager />;
      case 'live-follows':
        return <LiveFollows />;
      case 'user-approval':
        return <UserApproval />;
      default:
        return <DataSources />;
    }
  };

  const selectedItem = getSelectedItem();

  return (
    <div style={{
      height: '100vh',
      background: '#f5f5f5',
      overflow: 'hidden'
    }}>
      {/* Main Layout */}
      <Layout style={{
        height: '100vh',
        background: '#f5f5f5',
        overflow: 'hidden'
      }}>
        {/* Sidebar */}
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          width={320}
          collapsedWidth={80}
          style={{
            background: '#fff',
            borderRight: '1px solid #f0f0f0',
            boxShadow: '2px 0 8px rgba(0,0,0,0.06)',
            display: 'flex',
            flexDirection: 'column',
          }}
          trigger={null}
        >
          {/* Header Section */}
          <div style={{
            padding: collapsed ? '16px 8px' : '20px 16px 16px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff',
            position: 'relative',
            minHeight: collapsed ? '60px' : 'auto'
          }}>
            {!collapsed && (
              <>
                <Title level={4} style={{ color: '#fff', margin: 0, marginBottom: '8px' }}>
                  主数据管理
                </Title>
                <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px' }}>
                  按照依赖关系管理核心数据
                </Text>
              </>
            )}

            {/* Collapse Button */}
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                color: '#fff',
                border: 'none',
                width: '32px',
                height: '32px',
                borderRadius: '6px',
                background: 'rgba(255,255,255,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            />
          </div>

          {/* Menu Section */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Menu
              mode="inline"
              selectedKeys={[selectedKey]}
              onClick={({ key }) => setSelectedKey(key)}
              style={{
                border: 'none',
                background: 'transparent',
                padding: collapsed ? '8px 0' : '16px 0',
                flex: 1,
                minHeight: 0
              }}
              inlineCollapsed={collapsed}
              items={menuItems}
            />
          </div>
        </Sider>

        {/* Content Area */}
        <Layout style={{
          background: '#f5f5f5',
          padding: '24px',
          height: '100%'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            height: '100%',
            overflow: 'hidden'
          }}>
            <Content style={{
              background: 'transparent',
              padding: 0,
              height: '100%',
              overflow: 'auto'
            }}>
              {renderContent()}
            </Content>
          </div>
        </Layout>
      </Layout>
    </div>
  );
};

export default MasterData;
